import { defaultIfEmpty, filter, map, Observable, take, timer } from 'rxjs';

export interface OrttoTrackingUserDetails {
	sortKey: string;
	firstName: string;
	lastName: string;
	email: string;
}

export interface OrttoTalkSessionClient {
	setVerifiedSession?: (options: { token: string }) => void;
}

export interface OrttoTrackingClient {
	init: (apiKey: string, host: string, options: unknown) => void;
	track: (event: unknown) => void;
	talk?: OrttoTalkSessionClient;
}

export type OrttoTrackingSkipReason = 'missing-user' | 'missing-sdk';

/** Failed Talk token requests per user before `trackOrtto` stops retrying. */
export const ORTTO_TALK_TOKEN_MAX_ATTEMPTS = 2;

/**
 * Switch the already-booted Talk widget to a verified session.
 * `ap3c.init` ignores a second call once the boot snippet has run ("Ortto
 * tracking code already initialised"), so passing `talk.token` to it again
 * never applies the token. Returns false when the Talk client is not available.
 */
export function applyOrttoVerifiedTalkSession(
	ortto: OrttoTrackingClient | null | undefined,
	token: string | null | undefined
): boolean {
	const talk = ortto?.talk;
	if (!token || typeof talk?.setVerifiedSession !== 'function') {
		return false;
	}
	talk.setVerifiedSession({ token });
	return true;
}

/** Talk loads separately from `init`/`track`; wait up to ~10s for it. */
export const ORTTO_TALK_POLL_MS = 250;
export const ORTTO_TALK_MAX_POLLS = 40;

/**
 * Apply the verified Talk session once `ap3c.talk.setVerifiedSession` exists.
 * Emits true when applied, false when Talk never became available.
 */
export function applyOrttoVerifiedTalkSessionWhenReady(
	getOrtto: () => OrttoTrackingClient | null | undefined,
	token: string
): Observable<boolean> {
	return timer(ORTTO_TALK_POLL_MS, ORTTO_TALK_POLL_MS).pipe(
		take(ORTTO_TALK_MAX_POLLS),
		map(() => applyOrttoVerifiedTalkSession(getOrtto(), token)),
		filter(applied => applied),
		take(1),
		defaultIfEmpty(false)
	);
}

/** True while a user's failed Talk token requests are below the retry cap. */
export function canRetryOrttoTalkToken(failedAttempts: number): boolean {
	return failedAttempts < ORTTO_TALK_TOKEN_MAX_ATTEMPTS;
}

/**
 * Match the signed-in user in a getUsers list by email.
 * Returns null when email or list is not ready — callers should defer tracking.
 */
export function matchOrttoUserByEmail(
	users: OrttoTrackingUserDetails[] | null | undefined,
	email: string | null | undefined
): OrttoTrackingUserDetails | null {
	if (!email || !Array.isArray(users)) {
		return null;
	}
	return users.find(user => user?.email === email) ?? null;
}

/** True when the Ortto client exposes callable init/track (not just the pre-SDK stub). */
export function isOrttoClientReady(
	ortto: OrttoTrackingClient | null | undefined
): ortto is OrttoTrackingClient {
	return (
		!!ortto &&
		typeof ortto.init === 'function' &&
		typeof ortto.track === 'function'
	);
}

/**
 * Dedupe successful Ortto identify/track by user sortKey.
 * Must NOT be used alone in `distinctUntilChanged` on the account/user stream —
 * that drops retries after a missing-sdk skip when the SDK loads later.
 */
export function shouldSkipOrttoTrackForSortKey(
	lastSuccessfullyTrackedSortKey: string | null | undefined,
	sortKey: string
): boolean {
	return lastSuccessfullyTrackedSortKey === sortKey;
}

/**
 * Decide whether Ortto tracking can proceed.
 * - `missing-user`: expected while session/users settle — do not warn
 * - `missing-sdk`: user ready but Ortto client missing/not ready — warn + retry
 * - `null`: ready to track
 */
export function resolveOrttoTrackingSkip(
	ortto: OrttoTrackingClient | null | undefined,
	userDetails: OrttoTrackingUserDetails | null | undefined
): OrttoTrackingSkipReason | null {
	if (!userDetails) {
		return 'missing-user';
	}
	if (!isOrttoClientReady(ortto)) {
		return 'missing-sdk';
	}
	return null;
}
